import { type FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Button, Form, Spinner } from 'react-bootstrap';
import { BsPlus, BsStars } from 'react-icons/bs';
import cx from 'clsx';

import { type RootState } from '@editor2/reducers/root';
import { type EocCheckinChatbot, type KeyConcept as KeyConceptType } from '@editor2/types/pageElements';
import { updateElement } from '@editor2/reducers/editor/editorSlice';
import { generateEocCheckinConcepts } from '@editor2/reducers/editor/effects';
import { useAppDispatch } from '@editor2/store';
import { useDebouncedDispatch } from '@editor2/hooks/useDebouncedDispatch';
import { elementCommonStyles } from '@editor2/styles/element';
import Metadata from '@editor2/components/Metadata';
import ButtonTooltip from '@editor2/components/ButtonTooltip';

import KeyConcept from './KeyConcept';
import { eocCheckinChatbotElement } from './styles';
import { findChapterFamilyId, normalizeConcept } from './helpers';

interface Props {
	element: EocCheckinChatbot;
	courseId: number;
	pageId: string;
}

const EocCheckinChatbotElement: FC<Props> = ({ element, courseId, pageId }) => {
	const dispatch = useAppDispatch();
	const debouncedDispatch = useDebouncedDispatch();

	const { chapters, page_groups } = useSelector((state: RootState) => state.courses[courseId]);

	const commonPayload = { elementId: element.family_id, courseId };
	const keyConcepts = element.key_concepts ?? [];
	const isGenerating = !!element.isGeneratingConcepts;

	const chapterFamilyId = useMemo(
		() => findChapterFamilyId(chapters, page_groups, pageId),
		[chapters, page_groups, pageId]
	);

	const onChange = (value: Partial<EocCheckinChatbot>) => {
		dispatch(updateElement({ ...commonPayload, ...value }));
	};

	const onDebouncedChange = (value: Partial<EocCheckinChatbot>) => {
		debouncedDispatch(updateElement({ ...commonPayload, ...value }));
	};

	const handleAddConcept = () => {
		const newConcept: KeyConceptType = { concept: '', description: '' };
		onChange({ key_concepts: [...keyConcepts, newConcept] });
	};

	const handleGenerate = () => {
		if (!chapterFamilyId || isGenerating) return;
		dispatch(
			generateEocCheckinConcepts({
				courseId,
				elementId: element.family_id,
				chapterFamilyId
			})
		);
	};

	const handleNumberChange = (field: 'max_questions' | 'time_limit_seconds', raw: string) => {
		const trimmed = raw.trim();
		const parsed = trimmed === '' ? null : Number(trimmed);
		const value =
			parsed === null || (Number.isFinite(parsed) && parsed >= 1) ? parsed : null;
		onChange({ [field]: value } as Partial<EocCheckinChatbot>);
	};

	return (
		<Form className={cx(eocCheckinChatbotElement, elementCommonStyles)}>
			<Metadata
				courseId={courseId}
				metadata={element.metadata}
				onDebouncedChange={onDebouncedChange}
			/>

			<div className="settingsRow">
				<Form.Group className="setting">
					<Form.Label>Max questions</Form.Label>
					<Form.Control
						type="number"
						min={1}
						placeholder="Default"
						value={element.max_questions ?? ''}
						onChange={(e) => handleNumberChange('max_questions', e.target.value)}
					/>
				</Form.Group>

				<Form.Group className="setting">
					<Form.Label>Time limit (seconds)</Form.Label>
					<Form.Control
						type="number"
						min={1}
						placeholder="Default"
						value={element.time_limit_seconds ?? ''}
						onChange={(e) => handleNumberChange('time_limit_seconds', e.target.value)}
					/>
				</Form.Group>
			</div>

			<div className="sectionLabel">Key concepts</div>
			<div className="sectionHelp">
				The student must demonstrate each key concept before the check-in is complete. Populate
				these manually, or generate suggestions from the chapter content and adjust them.
			</div>

			{keyConcepts.length === 0 ? (
				<div className="emptyConcepts">No key concepts yet.</div>
			) : (
				<div className="conceptsList">
					{keyConcepts.map((concept, index) => (
						<KeyConcept
							// Concepts have no stable id; index is the positional key.
							key={`key-concept-${index}`}
							element={element}
							concept={normalizeConcept(concept)}
							index={index}
							courseId={courseId}
						/>
					))}
				</div>
			)}

			<div className="conceptActions">
				<Button variant="outline-secondary" size="sm" onClick={handleAddConcept}>
					<BsPlus />
					Add key concept
				</Button>

				<ButtonTooltip
					message={
						chapterFamilyId
							? 'Generate suggested key concepts from this chapter’s content and add them to the list'
							: 'Unable to determine the chapter for this element'
					}>
					<Button
						variant="outline-primary"
						size="sm"
						onClick={handleGenerate}
						disabled={isGenerating || !chapterFamilyId}>
						{isGenerating ? (
							<>
								<Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />{' '}
								Generating…
							</>
						) : (
							<>
								<BsStars />
								Generate from chapter content
							</>
						)}
					</Button>
				</ButtonTooltip>
			</div>
		</Form>
	);
};

export default EocCheckinChatbotElement;
